import type { FeatureFlagsPlugin } from '~/plugins/featureFlags';

const imageConfiguration = {
  widthSizes: {
    small: '480x',
    medium: '960x',
    large: '1440x',
    original: 'original',
  },

  screenWidthSizes: {
    small: '600w',
    medium: '980w',
    large: '1366w',
  },

  requestedSize: '_WIDTH_x_HEIGHT_',

  wrongAuthor: 'author/author',
  rightAuthor: 'author',
};

const getAttr = <T extends Record<string, any>>(obj: T, attr: string) => {
  return obj[`@${attr}`] ?? obj[attr];
};
const getRenditionsUrl = (mgnlImagePath: string, image: any, size: string, screenWidthSize: string) => {
  const path = getAttr(image, 'path');
  return { src: `${mgnlImagePath}/.imaging/mte/kikstorefront/${size}/dam${path}/jcr:content/${image.metadata?.fileName}`, width: screenWidthSize };
};
const getRenditionsUrls = (mgnlImagePath: string, image: any, mobileImage: any) => {
  return [
    getRenditionsUrl(mgnlImagePath, mobileImage ?? image, 'small', imageConfiguration.screenWidthSizes.small),
    getRenditionsUrl(mgnlImagePath, mobileImage ?? image, 'medium', imageConfiguration.screenWidthSizes.medium),
    getRenditionsUrl(mgnlImagePath, image, 'large', imageConfiguration.screenWidthSizes.large),
  ];
};
const getMobileUrls = (mgnlImagesPath: string, image: any, aspectRatio: any, imageSet: any, mobileImage: any, mobileAspectRatio: string = imageConfiguration.widthSizes.original) => {
  const largeUrl = aspectRatio === imageConfiguration.widthSizes.original ? getRenditionsUrl(mgnlImagesPath, image, 'large', imageConfiguration.screenWidthSizes.large) : { src: `${mgnlImagesPath}${imageSet[aspectRatio].replace(imageConfiguration.requestedSize, imageConfiguration.widthSizes.large)}`, width: imageConfiguration.screenWidthSizes.large };
  if (mobileImage.focal?.point?.urls) {
    const mobileImageSet = mobileImage.focal.point.urls;
    return [
      mobileAspectRatio != imageConfiguration.widthSizes.original ? { src: `${mgnlImagesPath}${mobileImageSet[mobileAspectRatio].replace(imageConfiguration.requestedSize, imageConfiguration.widthSizes.small)}`, width: imageConfiguration.screenWidthSizes.small } : getRenditionsUrl(mgnlImagesPath, mobileImage, 'small', imageConfiguration.screenWidthSizes.small),
      mobileAspectRatio != imageConfiguration.widthSizes.original ? { src: `${mgnlImagesPath}${mobileImageSet[mobileAspectRatio].replace(imageConfiguration.requestedSize, imageConfiguration.widthSizes.medium)}`, width: imageConfiguration.screenWidthSizes.medium } : getRenditionsUrl(mgnlImagesPath, mobileImage, 'medium', imageConfiguration.screenWidthSizes.medium),
      largeUrl,
    ];
  }
  return [
    { src: `${mgnlImagesPath}${mobileImage['@link']}`, width: imageConfiguration.screenWidthSizes.small },
    { src: `${mgnlImagesPath}${mobileImage['@link']}`, width: imageConfiguration.screenWidthSizes.medium },
    largeUrl,
  ];
};
const getUrls = (mgnlImagesPath: string, image: any, aspectRatio: string, imageSet: any, mobileAspectRatio?: string) => {
  if (aspectRatio === imageConfiguration.widthSizes.original) {
    return [
      mobileAspectRatio ? { src: `${mgnlImagesPath}${imageSet[mobileAspectRatio ?? aspectRatio].replace(imageConfiguration.requestedSize, imageConfiguration.widthSizes.small)}`, width: imageConfiguration.screenWidthSizes.small } : getRenditionsUrl(mgnlImagesPath, image, 'small', imageConfiguration.screenWidthSizes.small),
      getRenditionsUrl(mgnlImagesPath, image, 'medium', imageConfiguration.screenWidthSizes.medium),
      getRenditionsUrl(mgnlImagesPath, image, 'large', imageConfiguration.screenWidthSizes.large),
    ];
  }

  if (mobileAspectRatio === imageConfiguration.widthSizes.original) {
    return [
      getRenditionsUrl(mgnlImagesPath, image, 'small', imageConfiguration.screenWidthSizes.small),
      getRenditionsUrl(mgnlImagesPath, image, 'medium', imageConfiguration.screenWidthSizes.medium),
      { src: `${mgnlImagesPath}${imageSet[aspectRatio].replace(imageConfiguration.requestedSize, imageConfiguration.widthSizes.large)}`, width: imageConfiguration.screenWidthSizes.large },
    ]
    ;
  }

  return [
    { src: `${mgnlImagesPath}${imageSet[mobileAspectRatio ?? aspectRatio].replace(imageConfiguration.requestedSize, imageConfiguration.widthSizes.small)}`, width: imageConfiguration.screenWidthSizes.small },
    { src: `${mgnlImagesPath}${imageSet[mobileAspectRatio ?? aspectRatio].replace(imageConfiguration.requestedSize, imageConfiguration.widthSizes.medium)}`, width: imageConfiguration.screenWidthSizes.medium },
    { src: `${mgnlImagesPath}${imageSet[aspectRatio].replace(imageConfiguration.requestedSize, imageConfiguration.widthSizes.large)}`, width: imageConfiguration.screenWidthSizes.large },
  ];
};

const getSrcSetImages = (image: any, mgnlImagesPath: string, aspectRatio: string = imageConfiguration.widthSizes.original, mobileAspectRatio?: string, mobileImage?: any) => {
  if (!image?.focal?.point?.urls || image.metadata.format == 'image/gif') {
    return [];
  }
  const imageSet = image.focal.point.urls;

  if (mobileImage) {
    return getMobileUrls(mgnlImagesPath, image, aspectRatio, imageSet, mobileImage, mobileAspectRatio);
  }
  return getUrls(mgnlImagesPath, image, aspectRatio, imageSet, mobileAspectRatio);
};

export default {
  getLink: (url: string | { '@path': string }, intLinkParam: string) => {
    const path = typeof url === 'string' ? url : url?.['@path'];

    return `${path?.replace(/^\/[A-Z]{2,}\/(Home)/, '') || '/'}${intLinkParam ? `?int_link=${intLinkParam}` : '?int_link'}`;
  },
  getDefaultSrcAsset: (image, mgnlImagesPath: string): string => {
    if (!image?.['@link']) {
      // Can't resort to '/' since nuxt-img will try to load the full document

      return '/img/error/error.svg';
    }
    if (!image?.metadata || !image || image.metadata.format !== 'image/jpeg') {
      const src = { src: `${mgnlImagesPath}${image['@link']}` };
      return src.src.replace(imageConfiguration.wrongAuthor, imageConfiguration.rightAuthor);
    }
    const src = getRenditionsUrl(mgnlImagesPath, image, 'medium', imageConfiguration.screenWidthSizes.medium);
    return src.src.replace(imageConfiguration.wrongAuthor, imageConfiguration.rightAuthor);
  },
  getSrcSetImages,
  getSrcSetImagesAsString: (image, mgnlImagesPath: string, aspectRatio?: string, mobileAspectRatio?: string, mobileImage?): string => {
    let images;
    if (!image?.metadata || !image || image?.metadata?.fileName?.toLowerCase().endsWith('.svg')) {
      return '';
    }
    if (!aspectRatio) {
      images = getRenditionsUrls(mgnlImagesPath, image, mobileImage);
      return images?.map(({ src, width }) => `${src.replace(imageConfiguration.wrongAuthor, imageConfiguration.rightAuthor)} ${width}`).join(',');
    }
    images = getSrcSetImages(image, mgnlImagesPath, aspectRatio, mobileAspectRatio, mobileImage);

    if (!images.length) {
      return '';
    }
    return images?.map(({ src, width }) => `${src.replace(imageConfiguration.wrongAuthor, imageConfiguration.rightAuthor)} ${width}`).join(',');
  },
  getAltImage: (image, alt: string) => {
    if (alt) {
      return alt;
    }
    return image?.metadata?.caption;
  },
  getImageDimensions: image => ({
    width: image?.metadata?.width,
    height: image?.metadata?.height,
  }),
  getTitleImage: (image, title: string) => {
    if (title) {
      return title;
    }
    return image?.metadata?.title;
  },
  getControlsList: (mgnlProps: any) => {
    let list = '';
    if (!mgnlProps.enableFullscreen) {
      list += 'nofullscreen ';
    }
    if (!mgnlProps.enableDownload) {
      list += 'nodownload ';
    }
    return list;
  },
  getLinkAndName: (link: any, mgnlPath?: string, intLinkParam?: string) => {
    let name;
    let url;
    let type;
    const switchable = link?.switchable;
    if (switchable) {
      const {
        page, external, download, field,
      } = switchable;

      type = field;

      switch (field) {
        case 'page':
          url = page['@path'];
          name = link.title ?? page.navigationTitle;
          break;
        case 'download':
          url = `${mgnlPath}${download?.['@link']}`;
          name = link.title;
          break;
        default:
          url = external;
          name = link.title ?? external;
      }
    }

    if (link.showCtaAsPdf && link.pdfLinkInDam) {
      url = `${mgnlPath}${link.pdfLinkInDam['@link']}`;
      type = 'external';
    }
    return {
      url: `${url?.replace(/^\/[A-Z]{2,}\/(Home)/, '') || '/'}${type === 'page' ? (!intLinkParam ? '?int_link' : `?int_link=${intLinkParam.replaceAll(' ', '_')}`) : ''}`,
      name,
      type,
      target: link?.linkTargetMode ?? '_blank',
      emphasis: link?.emphasis ?? undefined,
      showLinkKiosk: link?.showLinkKiosk ?? true,
      showLinkCatalog: link?.showLinkCatalog ?? true,
    };
  },
  getExtendedLinkAndName: (link, mgnlPath: string) => {
    let mode;
    let url;
    let type;
    const switchable = link?.switchable;
    const switchableMode = link?.switchableMode;
    if (switchable) {
      const {
        page, external, download, field,
      } = switchable;

      type = field;

      switch (field) {
        case 'page':
          url = page['@path'];
          break;
        case 'download':
          url = `${mgnlPath}${download?.['@link']}`;
          break;
        default:
          url = external;
      }
    }

    if (switchableMode) {
      const {
        image, title, field,
      } = switchableMode;

      switch (field) {
        case 'title':
          mode = {
            type: 'title',
            title,
          };
          break;
        case 'image':
          mode = {
            type: 'image',
            src: image?.['@link'] ? `${mgnlPath}${image['@link']}` : undefined,
            title: switchableMode?.imageTitle ?? '',
            alt: switchableMode?.alt ?? '',
          };
          break;
        default:
          mode = {
            type: 'title',
            title: '',
          };
      }
    }

    return {
      url: url?.replace(/^\/[A-Z]{2,}\/(Home)/, '') || '/',
      mode,
      type,
      target: link?.linkTargetMode ?? '_blank',
    };
  },
  showKioskOrCatalog: ($flags: FeatureFlagsPlugin, kioskProp: boolean, catalogProp: boolean) => ((($flags.hasnt('enabledKiosk') || ($flags.has('enabledKiosk') && kioskProp))) && ($flags.hasnt('shop.catalog.enabled') || ($flags.has('shop.catalog.enabled') && catalogProp))),
  // Workaround necessary, Magnolia has no OOB solution to resolve RTE links with multisite module, for more info check the helpdesk support history: https://jira.magnolia-cms.com/servicedesk/customer/portal/6/HELPDESK-5461
  resolveRichTextContentAssetLinks: (content, host = '') => {
    return content?.replaceAll('="/author/', `="${host}/`);
  },
  getHexColor: (color?: number) => (color ? (`#${(16777216 + color)?.toString(16)?.padStart(6, '0')}`) : '#e7e7e6'),
  migratePageLinkToCtaLink: (mgnlProps, props?: any) => {
    if (props) {
      mgnlProps.ctaLink = [{
        pdfLinkInDam: props.pdfLinkInDam ?? undefined,
        showCtaAsPdf: props.showCtaAsPdf,
        linkTargetMode: props.ctaTargetMode,
        ctaText: props.ctaText,
        switchable: {
          field: props.pageLink?.['@path'] ? 'page' : 'external',
          external: props.pageLink,
          page: {
            '@path': props.pageLink?.['@path'],
          },
        },
      }];
    }
    else {
      mgnlProps.slides?.forEach((slide) => {
        if (slide.pageLink && !slide.ctaLink) {
          slide.ctaLink = [{
            pdfLinkInDam: slide.pdfLinkInDam ?? undefined,
            showCtaAsPdf: slide.showCtaAsPdf,
            linkTargetMode: slide.linkTargetMode,
            ctaText: slide.ctaText ?? '',
            switchable: {
              field: slide.pageLink?.['@path'] ? 'page' : 'external',
              external: slide.pageLink,
              page: {
                '@path': slide.pageLink?.['@path'],
              },
            },
          }];
        }
      });
    }
  },
};
